/**
 * Mage (道士) Renderer - Specialized renderer with attack animation
 * Features:
 * - Procedural spell casting animation via custom shaders
 * - Staff sway and gourd glow effect
 * - Elegant Taoist casting motion
 * - AOE attack visual cues
 */

import * as THREE from 'three';
import type { FactionId, Entity } from '@/types';
import { EventBus } from '@/core/events/EventBus';
import { COLORS, FACTION_COLORS } from './unitConstants';

const MAX_MAGES = 500;

// Animation group IDs encoded in vertex attribute
export const MAGE_ANIM_GROUP = {
  STATIC: 0,       // No animation
  BODY: 1,         // Body - lean during attack
  HEAD: 2,         // Head - follows body
  LEFT_ARM: 3,     // Left arm (holding staff)
  RIGHT_ARM: 4,    // Right arm (casting hand)
  LEFT_LEG: 5,     // Left leg
  RIGHT_LEG: 6,    // Right leg
  STAFF: 7,        // Staff - sway and raise
  GOURD: 8,        // Gourd - glow during attack
  ROBE: 9,         // Flowing robe
  HAIR: 10,        // Hair and accessories
};

interface MageData {
  entity: Entity;
  x: number;
  y: number;
  z: number;
  factionId: FactionId;
  rotation: number;
  speed: number;           // Movement speed for walk animation
  animPhase: number;       // Animation phase offset
  attackTime: number;      // Time when attack started (-1 = not attacking)
  attackDuration: number;  // Duration of attack animation
}

// Vertex shader with attack animation
const mageVertexShader = `
  attribute vec3 color;
  attribute float animGroup;

  uniform float uTime;

  // Per-instance attributes
  attribute float instanceSpeed;
  attribute float instanceAnimPhase;
  attribute float instanceAttackTime;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;
  varying float vAttackGlow;

  // Animation parameters
  const float PI = 3.14159265359;
  const float WALK_FREQUENCY = 3.0;
  const float ATTACK_DURATION = 0.7;  // Attack animation duration in seconds

  vec3 animateVertex(vec3 pos, float group, float time, float speed, float phase, float attackTime, out float attackGlow) {
    vec3 animated = pos;
    attackGlow = 0.0;

    // Calculate attack progress (0 = not attacking, 0-1 = attack progress)
    float attackProgress = 0.0;
    float attackPhase = 0.0;
    if (attackTime >= 0.0) {
      float timeSinceAttack = time - attackTime;
      if (timeSinceAttack < ATTACK_DURATION) {
        attackProgress = timeSinceAttack / ATTACK_DURATION;
        // Attack phases: 0-0.35 = channel, 0.35-0.55 = release, 0.55-1.0 = recover
        if (attackProgress < 0.35) {
          attackPhase = attackProgress / 0.35;  // 0-1 channel
        } else if (attackProgress < 0.55) {
          attackPhase = 1.0 + (attackProgress - 0.35) / 0.2;  // 1-2 release
        } else {
          attackPhase = 2.0 + (attackProgress - 0.55) / 0.45;  // 2-3 recover
        }
      }
    }

    // Walking animation
    float walkIntensity = clamp(speed, 0.0, 1.0);
    float t = time * WALK_FREQUENCY + phase;

    // Idle sway - graceful for mage
    float idleSway = sin(time * 1.2 + phase) * 0.003 * (1.0 - walkIntensity);
    float idleBreath = sin(time * 0.8 + phase) * 0.002 * (1.0 - walkIntensity);

    if (group < 0.5) {
      // STATIC - no animation
      return animated;
    }

    // Apply idle sway to all animated parts
    animated.z += idleSway;

    if (group < 1.5) {
      // BODY - lean back during channel, forward during release
      float walkBob = sin(t * 2.0) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float leanAmount = 0.0;
        float sideSwayAmount = 0.0;
        if (attackPhase < 1.0) {
          // Channel - lean back slightly, prepare
          leanAmount = -0.012 * attackPhase;
          sideSwayAmount = sin(attackPhase * PI) * 0.005;
        } else if (attackPhase < 2.0) {
          // Release - lean forward with power
          float releasePhase = attackPhase - 1.0;
          leanAmount = -0.012 + 0.035 * releasePhase;
          sideSwayAmount = 0.0;
        } else {
          // Recover - return to neutral smoothly
          float recoverPhase = attackPhase - 2.0;
          leanAmount = 0.023 * (1.0 - recoverPhase);
        }
        animated.z += leanAmount;
        animated.x += sideSwayAmount;
        animated.y -= abs(leanAmount) * 0.3;
      }
    }
    else if (group < 2.5) {
      // HEAD - follows body with slight delay, look up during channel
      float walkBob = sin(t * 2.0 + 0.2) * 0.0015 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float leanAmount = 0.0;
        float lookUp = 0.0;
        if (attackPhase < 1.0) {
          // Channel - head tilts back (looking up at magic)
          leanAmount = -0.008 * attackPhase;
          lookUp = 0.008 * sin(attackPhase * PI * 0.5);
        } else if (attackPhase < 2.0) {
          // Release - head follows body forward
          float releasePhase = attackPhase - 1.0;
          leanAmount = -0.008 + 0.028 * releasePhase;
          lookUp = 0.008 * (1.0 - releasePhase);
        } else {
          float recoverPhase = attackPhase - 2.0;
          leanAmount = 0.02 * (1.0 - recoverPhase);
        }
        animated.z += leanAmount;
        animated.y += lookUp;
      }
    }
    else if (group < 3.5) {
      // LEFT_ARM (staff arm) - holds staff steady, slight raise during attack
      float walkSwing = sin(t) * 0.006 * walkIntensity;
      animated.z += walkSwing * 0.5;

      if (attackPhase > 0.0) {
        float raiseAmount = 0.0;
        float forwardAmount = 0.0;
        if (attackPhase < 1.0) {
          // Channel - arm rises with staff
          raiseAmount = 0.015 * attackPhase;
          forwardAmount = 0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Release - arm pushes forward
          float releasePhase = attackPhase - 1.0;
          raiseAmount = 0.015 - 0.005 * releasePhase;
          forwardAmount = 0.008 + 0.015 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          raiseAmount = 0.01 * (1.0 - recoverPhase);
          forwardAmount = 0.023 * (1.0 - recoverPhase);
        }
        animated.y += raiseAmount;
        animated.z += forwardAmount;
      }
    }
    else if (group < 4.5) {
      // RIGHT_ARM (casting hand) - dramatic gesture during spell
      float walkSwing = -sin(t) * 0.006 * walkIntensity;
      animated.z += walkSwing * 0.5;

      if (attackPhase > 0.0) {
        float raiseAmount = 0.0;
        float forwardAmount = 0.0;
        float outwardAmount = 0.0;
        if (attackPhase < 1.0) {
          // Channel - hand raises with fingers spread
          raiseAmount = 0.025 * attackPhase;
          forwardAmount = 0.015 * attackPhase;
          outwardAmount = 0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Release - hand thrusts forward dramatically
          float releasePhase = attackPhase - 1.0;
          raiseAmount = 0.025 - 0.01 * releasePhase;
          forwardAmount = 0.015 + 0.04 * releasePhase;
          outwardAmount = 0.01 + 0.005 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          raiseAmount = 0.015 * (1.0 - recoverPhase);
          forwardAmount = 0.055 * (1.0 - recoverPhase);
          outwardAmount = 0.015 * (1.0 - recoverPhase);
        }
        animated.y += raiseAmount;
        animated.z += forwardAmount;
        animated.x += outwardAmount;
      }
    }
    else if (group < 5.5) {
      // LEFT_LEG - stance during cast
      float cycle = t;
      float lift = max(sin(cycle), 0.0) * 0.006 * walkIntensity;
      float swing = sin(cycle) * 0.005 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      // Brace during attack
      if (attackPhase >= 0.5 && attackPhase < 2.0) {
        animated.z -= 0.004;
      }
    }
    else if (group < 6.5) {
      // RIGHT_LEG - step forward during release
      float cycle = t + PI;
      float lift = max(sin(cycle), 0.0) * 0.006 * walkIntensity;
      float swing = sin(cycle) * 0.005 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      // Step forward during release
      if (attackPhase >= 1.0 && attackPhase < 2.0) {
        float releasePhase = attackPhase - 1.0;
        animated.z += 0.012 * releasePhase;
      } else if (attackPhase >= 2.0) {
        float recoverPhase = attackPhase - 2.0;
        animated.z += 0.012 * (1.0 - recoverPhase);
      }
    }
    else if (group < 7.5) {
      // STAFF - sway and raise during attack
      float walkSway = sin(t * 0.5) * 0.004 * walkIntensity;
      float idleStaffSway = sin(time * 0.8 + phase) * 0.003 * (1.0 - walkIntensity);
      animated.x += walkSway + idleStaffSway;

      if (attackPhase > 0.0) {
        float raiseAmount = 0.0;
        float forwardAmount = 0.0;
        float tiltAmount = 0.0;
        if (attackPhase < 1.0) {
          // Channel - staff rises, tilts back
          raiseAmount = 0.025 * attackPhase;
          forwardAmount = -0.01 * attackPhase;
          tiltAmount = 0.015 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Release - staff swings forward
          float releasePhase = attackPhase - 1.0;
          raiseAmount = 0.025 - 0.01 * releasePhase;
          forwardAmount = -0.01 + 0.045 * releasePhase;
          tiltAmount = 0.015 - 0.025 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          raiseAmount = 0.015 * (1.0 - recoverPhase);
          forwardAmount = 0.035 * (1.0 - recoverPhase);
          tiltAmount = -0.01 * (1.0 - recoverPhase);
        }
        animated.y += raiseAmount;
        animated.z += forwardAmount;
        // Tilt effect via x offset based on y position
        animated.x += tiltAmount * (pos.y - 0.4);
      }
    }
    else if (group < 8.5) {
      // GOURD - shake and glow during attack
      float idleGourdSway = sin(time * 1.0 + phase) * 0.002 * (1.0 - walkIntensity);
      animated.x += idleGourdSway;

      if (attackPhase > 0.0) {
        float raiseAmount = 0.0;
        float shakeAmount = 0.0;
        if (attackPhase < 1.0) {
          // Channel - gourd rises, starts shaking
          raiseAmount = 0.02 * attackPhase;
          shakeAmount = sin(attackPhase * 20.0) * 0.004 * attackPhase;
          attackGlow = attackPhase * 0.5;
        } else if (attackPhase < 2.0) {
          // Release - gourd shakes intensely, maximum glow
          float releasePhase = attackPhase - 1.0;
          raiseAmount = 0.02 - 0.008 * releasePhase;
          shakeAmount = sin(releasePhase * 30.0) * 0.006;
          attackGlow = 0.5 + releasePhase * 0.5;
        } else {
          // Recover - glow fades
          float recoverPhase = attackPhase - 2.0;
          raiseAmount = 0.012 * (1.0 - recoverPhase);
          attackGlow = 1.0 * (1.0 - recoverPhase);
        }
        animated.y += raiseAmount;
        animated.x += shakeAmount;
        // Apply same staff movement to gourd
        float forwardAmount = 0.0;
        if (attackPhase < 1.0) {
          forwardAmount = -0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          float releasePhase = attackPhase - 1.0;
          forwardAmount = -0.01 + 0.045 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          forwardAmount = 0.035 * (1.0 - recoverPhase);
        }
        animated.z += forwardAmount;
      }
    }
    else if (group < 9.5) {
      // ROBE - flowing motion during attack
      float walkFlow = sin(t * 1.5) * 0.003 * walkIntensity;
      float idleFlow = sin(time * 0.6 + phase) * 0.004 * (1.0 - walkIntensity);
      animated.z += walkFlow + idleFlow;

      if (attackPhase > 0.0) {
        float flowAmount = 0.0;
        if (attackPhase < 1.0) {
          // Channel - robe flows back slightly
          flowAmount = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Release - robe billows forward
          float releasePhase = attackPhase - 1.0;
          flowAmount = -0.008 + 0.025 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          flowAmount = 0.017 * (1.0 - recoverPhase);
        }
        animated.z += flowAmount;
        // Add side flow based on position
        animated.x += sin(pos.y * 10.0 + time * 5.0) * abs(flowAmount) * 0.5;
      }
    }
    else if (group < 10.5) {
      // HAIR - flows during movement
      float hairFlow = sin(t * 1.2 + phase) * 0.004 * walkIntensity;
      float idleHairSway = sin(time * 0.7 + phase) * 0.002 * (1.0 - walkIntensity);
      animated.z -= hairFlow + idleHairSway;

      if (attackPhase > 0.0) {
        float flowAmount = 0.0;
        if (attackPhase < 1.0) {
          // Channel - hair flows back
          flowAmount = -0.006 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Release - hair whips forward
          float releasePhase = attackPhase - 1.0;
          flowAmount = -0.006 + 0.018 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          flowAmount = 0.012 * (1.0 - recoverPhase);
        }
        animated.z += flowAmount;
      }
    }

    return animated;
  }

  void main() {
    vColor = color;
    vNormal = normalize(normalMatrix * normal);

    // Get instance-specific animation parameters
    float speed = instanceSpeed;
    float phase = instanceAnimPhase;
    float attackTime = instanceAttackTime;

    // Animate vertex
    float attackGlow;
    vec3 animatedPosition = animateVertex(position, animGroup, uTime, speed, phase, attackTime, attackGlow);
    vAttackGlow = attackGlow;

    vec4 worldPosition = instanceMatrix * vec4(animatedPosition, 1.0);
    vWorldPosition = worldPosition.xyz;

    vec4 mvPosition = modelViewMatrix * worldPosition;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Fragment shader with faction colors and glow effect
const mageFragmentShader = `
  uniform vec3 primaryColor;
  uniform vec3 secondaryColor;
  uniform vec3 ambientLightColor;
  uniform vec3 directionalLightColor;
  uniform vec3 directionalLightDirection;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;
  varying float vAttackGlow;

  void main() {
    vec3 finalColor = vColor;

    // Faction color replacement
    if (vColor.r > 0.9 && vColor.g < 0.2 && vColor.b < 0.2) {
      finalColor = primaryColor;
    } else if (vColor.g > 0.9 && vColor.r < 0.2 && vColor.b < 0.2) {
      finalColor = secondaryColor;
    }

    // Gourd glow effect (gold color area - detect by checking for gold-ish hue)
    bool isGourd = (vColor.r > 0.8 && vColor.g > 0.55 && vColor.g < 0.75 && vColor.b < 0.25);
    if (isGourd && vAttackGlow > 0.0) {
      // Add mystical glow
      vec3 glowColor = vec3(0.3, 0.9, 0.9);  // Teal/cyan mystical glow
      float glowIntensity = vAttackGlow * 0.6;
      finalColor = mix(finalColor, glowColor, glowIntensity);
      finalColor += glowColor * glowIntensity * 0.4;  // Additive glow
    }

    // Lighting
    vec3 normal = normalize(vNormal);
    vec3 lightDir = normalize(directionalLightDirection);

    float NdotL = dot(normal, lightDir);
    float diff = max(NdotL, 0.0);
    float wrap = max(NdotL * 0.5 + 0.5, 0.0);

    // Rim lighting - more pronounced for mystical effect
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float rim = 1.0 - max(dot(viewDir, normal), 0.0);
    rim = pow(rim, 3.0) * 0.2;

    // Add mystical rim glow during attack
    if (vAttackGlow > 0.0) {
      rim += vAttackGlow * 0.15;
    }

    // Combine lighting
    vec3 ambient = ambientLightColor * 0.55;
    vec3 diffuse = directionalLightColor * diff * 0.35;
    vec3 fill = directionalLightColor * wrap * 0.15;
    vec3 rimColor = vec3(0.8, 0.95, 0.95) * rim;  // Slight teal tint for mystical feel

    vec3 litColor = finalColor * (ambient + diffuse + fill) + rimColor;

    // Slight color variation
    litColor *= 0.95 + 0.1 * fract(sin(dot(vWorldPosition.xz, vec2(12.9898, 78.233))) * 43758.5453);

    gl_FragColor = vec4(litColor, 1.0);
  }
`;

export class MageRenderer {
  private scene: THREE.Scene;
  private instancedMeshes: Map<FactionId, THREE.InstancedMesh> = new Map();
  private mageData: MageData[] = [];
  private entityMap: Map<Entity, MageData> = new Map();
  private materials: Map<FactionId, THREE.ShaderMaterial> = new Map();
  private dummy = new THREE.Object3D();
  private time = 0;

  // Instance attributes for animation
  private speedAttribute: THREE.InstancedBufferAttribute | null = null;
  private phaseAttribute: THREE.InstancedBufferAttribute | null = null;
  private attackTimeAttribute: THREE.InstancedBufferAttribute | null = null;

  // Event listener cleanup
  private attackEventHandler: ((data: { attackerId: Entity }) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createGeometryAndMeshes();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for attack events to trigger attack animation
    this.attackEventHandler = (data: { attackerId: Entity }) => {
      const mage = this.entityMap.get(data.attackerId);
      if (mage) {
        mage.attackTime = this.time;
        mage.attackDuration = 0.7;
      }
    };
    EventBus.on('unit:attack', this.attackEventHandler);
  }

  private createGeometryAndMeshes(): void {
    const geometry = this.createMageGeometry();

    // Create instance attributes for animation
    const speeds = new Float32Array(MAX_MAGES);
    const phases = new Float32Array(MAX_MAGES);
    const attackTimes = new Float32Array(MAX_MAGES);
    for (let i = 0; i < MAX_MAGES; i++) {
      speeds[i] = 0;
      phases[i] = Math.random() * Math.PI * 2;
      attackTimes[i] = -1;  // Not attacking
    }

    this.speedAttribute = new THREE.InstancedBufferAttribute(speeds, 1);
    this.phaseAttribute = new THREE.InstancedBufferAttribute(phases, 1);
    this.attackTimeAttribute = new THREE.InstancedBufferAttribute(attackTimes, 1);

    geometry.setAttribute('instanceSpeed', this.speedAttribute);
    geometry.setAttribute('instanceAnimPhase', this.phaseAttribute);
    geometry.setAttribute('instanceAttackTime', this.attackTimeAttribute);

    // Create mesh for each faction
    for (const [factionId, colors] of Object.entries(FACTION_COLORS)) {
      const material = new THREE.ShaderMaterial({
        vertexShader: mageVertexShader,
        fragmentShader: mageFragmentShader,
        uniforms: {
          primaryColor: { value: colors.primary },
          secondaryColor: { value: colors.secondary },
          ambientLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightDirection: { value: new THREE.Vector3(0.5, 1.0, 0.3).normalize() },
          uTime: { value: 0 },
        },
      });
      this.materials.set(factionId as FactionId, material);

      const mesh = new THREE.InstancedMesh(geometry, material, MAX_MAGES);
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      this.instancedMeshes.set(factionId as FactionId, mesh);
      this.scene.add(mesh);
    }
  }

  private createMageGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const colors: THREE.Color[] = [];
    const animGroups: number[] = [];

    // ==================== 头部 ====================
    const head = new THREE.SphereGeometry(0.08, 10, 8);
    head.translate(0, 0.52, 0);
    parts.push(head);
    colors.push(COLORS.skin);
    animGroups.push(MAGE_ANIM_GROUP.HEAD);

    // 鼻子
    const nose = new THREE.SphereGeometry(0.012, 4, 3);
    nose.translate(0, 0.50, 0.075);
    parts.push(nose);
    colors.push(COLORS.skin);
    animGroups.push(MAGE_ANIM_GROUP.HEAD);

    // ==================== 发型 (道士发髻) ====================
    // 后脑头发
    const hairBack = new THREE.SphereGeometry(0.065, 8, 6);
    hairBack.scale(1.0, 1.1, 0.8);
    hairBack.translate(0, 0.54, -0.025);
    parts.push(hairBack);
    colors.push(COLORS.hair);
    animGroups.push(MAGE_ANIM_GROUP.HAIR);

    // 发髻 (顶部圆髻)
    const hairBun = new THREE.SphereGeometry(0.035, 6, 5);
    hairBun.scale(1.0, 1.4, 1.0);
    hairBun.translate(0, 0.635, -0.01);
    parts.push(hairBun);
    colors.push(COLORS.hair);
    animGroups.push(MAGE_ANIM_GROUP.HAIR);

    // 发髻底座
    const bunBase = new THREE.CylinderGeometry(0.025, 0.03, 0.02, 6);
    bunBase.translate(0, 0.60, -0.01);
    parts.push(bunBase);
    colors.push(COLORS.hair);
    animGroups.push(MAGE_ANIM_GROUP.HAIR);

    // 金簪 (横穿发髻)
    const hairpin = new THREE.CylinderGeometry(0.003, 0.003, 0.09, 4);
    hairpin.rotateZ(Math.PI / 2);
    hairpin.translate(0, 0.635, -0.01);
    parts.push(hairpin);
    colors.push(COLORS.gold);
    animGroups.push(MAGE_ANIM_GROUP.HAIR);

    // 簪头装饰 (左侧)
    const hairpinOrnamentL = new THREE.SphereGeometry(0.010, 5, 4);
    hairpinOrnamentL.translate(-0.045, 0.635, -0.01);
    parts.push(hairpinOrnamentL);
    colors.push(COLORS.gold);
    animGroups.push(MAGE_ANIM_GROUP.HAIR);

    // 簪头装饰 (右侧)
    const hairpinOrnamentR = new THREE.SphereGeometry(0.008, 5, 4);
    hairpinOrnamentR.translate(0.045, 0.635, -0.01);
    parts.push(hairpinOrnamentR);
    colors.push(COLORS.gold);
    animGroups.push(MAGE_ANIM_GROUP.HAIR);

    // 鬓发 (两侧垂发)
    for (const side of [-1, 1]) {
      const strand = new THREE.CylinderGeometry(0.008, 0.005, 0.10, 4);
      strand.rotateZ(side * 0.15);
      strand.translate(side * 0.065, 0.46, 0.02);
      parts.push(strand);
      colors.push(COLORS.hair);
      animGroups.push(MAGE_ANIM_GROUP.HAIR);
    }

    // ==================== 躯干 (修长道袍) ====================
    // 上身道袍 (肩部到腰部)
    const upperRobe = new THREE.CylinderGeometry(0.055, 0.07, 0.14, 8);
    upperRobe.translate(0, 0.36, 0);
    parts.push(upperRobe);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.BODY);

    // V领衣襟 (左)
    const collarLeft = new THREE.BoxGeometry(0.025, 0.08, 0.010);
    collarLeft.rotateZ(0.2);
    collarLeft.translate(-0.025, 0.39, 0.062);
    parts.push(collarLeft);
    colors.push(new THREE.Color(0x2d6b6b));
    animGroups.push(MAGE_ANIM_GROUP.BODY);

    // V领衣襟 (右)
    const collarRight = new THREE.BoxGeometry(0.025, 0.08, 0.010);
    collarRight.rotateZ(-0.2);
    collarRight.translate(0.025, 0.39, 0.062);
    parts.push(collarRight);
    colors.push(new THREE.Color(0x2d6b6b));
    animGroups.push(MAGE_ANIM_GROUP.BODY);

    // 内衬 (白色)
    const innerRobe = new THREE.BoxGeometry(0.015, 0.06, 0.008);
    innerRobe.translate(0, 0.38, 0.068);
    parts.push(innerRobe);
    colors.push(new THREE.Color(0xeeeeee));
    animGroups.push(MAGE_ANIM_GROUP.BODY);

    // 腰带 (深色)
    const belt = new THREE.CylinderGeometry(0.072, 0.072, 0.022, 8);
    belt.translate(0, 0.275, 0);
    parts.push(belt);
    colors.push(new THREE.Color(0x1a1a1a));
    animGroups.push(MAGE_ANIM_GROUP.BODY);

    // 腰带扣
    const beltBuckle = new THREE.BoxGeometry(0.02, 0.018, 0.012);
    beltBuckle.translate(0, 0.275, 0.072);
    parts.push(beltBuckle);
    colors.push(COLORS.gold);
    animGroups.push(MAGE_ANIM_GROUP.BODY);

    // 腰带垂饰
    const beltTassel = new THREE.CylinderGeometry(0.006, 0.004, 0.06, 4);
    beltTassel.translate(0.04, 0.23, 0.068);
    parts.push(beltTassel);
    colors.push(new THREE.Color(0x8b0000));
    animGroups.push(MAGE_ANIM_GROUP.BODY);

    // 下摆道袍 (修长优雅)
    const lowerRobe = new THREE.CylinderGeometry(0.07, 0.12, 0.22, 10);
    lowerRobe.translate(0, 0.14, 0);
    parts.push(lowerRobe);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.ROBE);

    // 下摆装饰边
    const hem = new THREE.TorusGeometry(0.115, 0.008, 4, 16);
    hem.rotateX(Math.PI / 2);
    hem.translate(0, 0.03, 0);
    parts.push(hem);
    colors.push(new THREE.Color(0x2d6b6b));
    animGroups.push(MAGE_ANIM_GROUP.ROBE);

    // 道袍褶皱纹理 (前面)
    for (let i = 0; i < 3; i++) {
      const fold = new THREE.BoxGeometry(0.006, 0.18, 0.004);
      fold.translate(-0.03 + i * 0.03, 0.14, 0.085 + i * 0.012);
      parts.push(fold);
      colors.push(new THREE.Color(0x358585));
      animGroups.push(MAGE_ANIM_GROUP.ROBE);
    }

    // ==================== 左臂 (持杖手) ====================
    const leftShoulderX = -0.075;
    const leftShoulderY = 0.40;

    // 左肩关节
    const leftShoulder = new THREE.SphereGeometry(0.028, 6, 5);
    leftShoulder.translate(leftShoulderX, leftShoulderY, 0);
    parts.push(leftShoulder);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.LEFT_ARM);

    // 左上臂
    const leftElbowX = leftShoulderX - 0.02;
    const leftElbowY = leftShoulderY - 0.07;
    const leftUpperArmAngle = Math.atan2(leftElbowX - leftShoulderX, leftShoulderY - leftElbowY);
    const leftUpperArm = new THREE.CylinderGeometry(0.024, 0.022, 0.07, 6);
    leftUpperArm.rotateZ(leftUpperArmAngle);
    leftUpperArm.translate((leftShoulderX + leftElbowX) / 2, (leftShoulderY + leftElbowY) / 2, 0);
    parts.push(leftUpperArm);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.LEFT_ARM);

    // 左肘关节
    const leftElbow = new THREE.SphereGeometry(0.022, 5, 4);
    leftElbow.translate(leftElbowX, leftElbowY, 0);
    parts.push(leftElbow);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.LEFT_ARM);

    // 左前臂+袖子
    const leftHandX = leftElbowX - 0.008;
    const leftHandY = leftElbowY - 0.085;
    const leftSleeveAngle = Math.atan2(leftHandX - leftElbowX, leftElbowY - leftHandY);
    const leftSleeve = new THREE.CylinderGeometry(0.022, 0.048, 0.085, 6);
    leftSleeve.rotateZ(leftSleeveAngle);
    leftSleeve.translate((leftElbowX + leftHandX) / 2, (leftElbowY + leftHandY) / 2, 0);
    parts.push(leftSleeve);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.LEFT_ARM);

    // 左手
    const leftHand = new THREE.SphereGeometry(0.016, 5, 4);
    leftHand.translate(leftHandX, leftHandY, 0);
    parts.push(leftHand);
    colors.push(COLORS.skin);
    animGroups.push(MAGE_ANIM_GROUP.LEFT_ARM);

    // ==================== 右臂 (施法手) ====================
    const rightShoulderX = 0.075;
    const rightShoulderY = 0.40;

    // 右肩关节
    const rightShoulder = new THREE.SphereGeometry(0.028, 6, 5);
    rightShoulder.translate(rightShoulderX, rightShoulderY, 0.01);
    parts.push(rightShoulder);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.RIGHT_ARM);

    // 右上臂
    const rightElbowX = rightShoulderX + 0.025;
    const rightElbowY = rightShoulderY - 0.065;
    const rightUpperArmAngle = Math.atan2(rightElbowX - rightShoulderX, rightShoulderY - rightElbowY);
    const rightUpperArm = new THREE.CylinderGeometry(0.024, 0.022, 0.07, 6);
    rightUpperArm.rotateZ(rightUpperArmAngle);
    rightUpperArm.translate((rightShoulderX + rightElbowX) / 2, (rightShoulderY + rightElbowY) / 2, 0.015);
    parts.push(rightUpperArm);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.RIGHT_ARM);

    // 右肘关节
    const rightElbow = new THREE.SphereGeometry(0.022, 5, 4);
    rightElbow.translate(rightElbowX, rightElbowY, 0.02);
    parts.push(rightElbow);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.RIGHT_ARM);

    // 右前臂+袖子
    const rightHandX = rightElbowX + 0.045;
    const rightHandY = rightElbowY - 0.06;
    const rightSleeveAngle = Math.atan2(rightHandX - rightElbowX, rightElbowY - rightHandY);
    const rightSleeve = new THREE.CylinderGeometry(0.022, 0.05, 0.08, 6);
    rightSleeve.rotateZ(rightSleeveAngle);
    rightSleeve.translate((rightElbowX + rightHandX) / 2, (rightElbowY + rightHandY) / 2, 0.03);
    parts.push(rightSleeve);
    colors.push(COLORS.mageTeal);
    animGroups.push(MAGE_ANIM_GROUP.RIGHT_ARM);

    // 右手
    const rightHand = new THREE.SphereGeometry(0.016, 5, 4);
    rightHand.translate(rightHandX, rightHandY, 0.04);
    parts.push(rightHand);
    colors.push(COLORS.skin);
    animGroups.push(MAGE_ANIM_GROUP.RIGHT_ARM);

    // 施法手指
    for (let i = 0; i < 3; i++) {
      const finger = new THREE.CylinderGeometry(0.004, 0.003, 0.02, 4);
      finger.rotateX(-0.5);
      finger.translate(rightHandX + 0.006 * (i - 1), rightHandY + 0.006, 0.055);
      parts.push(finger);
      colors.push(COLORS.skin);
      animGroups.push(MAGE_ANIM_GROUP.RIGHT_ARM);
    }

    // ==================== 脚部 ====================
    for (const xOff of [-0.035, 0.035]) {
      const foot = new THREE.BoxGeometry(0.028, 0.012, 0.040);
      foot.translate(xOff, 0.01, 0.035);
      parts.push(foot);
      colors.push(new THREE.Color(0x1a1a1a));
      animGroups.push(xOff < 0 ? MAGE_ANIM_GROUP.LEFT_LEG : MAGE_ANIM_GROUP.RIGHT_LEG);
    }

    // ==================== 葫芦法杖 ====================
    const staffX = leftHandX;
    const staffBaseY = leftHandY;

    // 杖身
    const shaftLength = 0.52;
    const shaft = new THREE.CylinderGeometry(0.012, 0.016, shaftLength, 6);
    shaft.translate(staffX, staffBaseY + shaftLength / 2, 0);
    parts.push(shaft);
    colors.push(COLORS.wood);
    animGroups.push(MAGE_ANIM_GROUP.STAFF);

    // 杖身木纹节点
    for (const yOffset of [0.08, 0.25, 0.40]) {
      const knot = new THREE.SphereGeometry(0.010, 4, 3);
      knot.translate(staffX, staffBaseY + yOffset, 0.010);
      parts.push(knot);
      colors.push(COLORS.woodDark);
      animGroups.push(MAGE_ANIM_GROUP.STAFF);
    }

    // ==================== 葫芦 ====================
    const gourdX = staffX;
    const gourdBaseY = staffBaseY + shaftLength;

    // 下葫芦 (大)
    const gourdLower = new THREE.SphereGeometry(0.035, 8, 6);
    gourdLower.scale(1.0, 1.15, 1.0);
    gourdLower.translate(gourdX, gourdBaseY + 0.02, 0);
    parts.push(gourdLower);
    colors.push(COLORS.gold);
    animGroups.push(MAGE_ANIM_GROUP.GOURD);

    // 上葫芦 (小)
    const gourdUpper = new THREE.SphereGeometry(0.025, 7, 5);
    gourdUpper.scale(1.0, 1.1, 1.0);
    gourdUpper.translate(gourdX, gourdBaseY + 0.095, 0);
    parts.push(gourdUpper);
    colors.push(COLORS.gold);
    animGroups.push(MAGE_ANIM_GROUP.GOURD);

    // 葫芦颈部
    const gourdNeck = new THREE.CylinderGeometry(0.013, 0.016, 0.018, 6);
    gourdNeck.translate(gourdX, gourdBaseY + 0.058, 0);
    parts.push(gourdNeck);
    colors.push(COLORS.gold);
    animGroups.push(MAGE_ANIM_GROUP.GOURD);

    // 葫芦塞
    const stopper = new THREE.CylinderGeometry(0.008, 0.010, 0.016, 5);
    stopper.translate(gourdX, gourdBaseY + 0.13, 0);
    parts.push(stopper);
    colors.push(COLORS.woodDark);
    animGroups.push(MAGE_ANIM_GROUP.GOURD);

    // 葫芦绳结
    const ropeKnot = new THREE.TorusGeometry(0.028, 0.004, 4, 12);
    ropeKnot.rotateX(Math.PI / 2);
    ropeKnot.translate(gourdX, gourdBaseY + 0.058, 0);
    parts.push(ropeKnot);
    colors.push(new THREE.Color(0x8b0000));
    animGroups.push(MAGE_ANIM_GROUP.GOURD);

    // 流苏
    const tasselCord = new THREE.CylinderGeometry(0.005, 0.003, 0.045, 4);
    tasselCord.translate(gourdX, gourdBaseY - 0.04, 0.025);
    parts.push(tasselCord);
    colors.push(new THREE.Color(0x8b0000));
    animGroups.push(MAGE_ANIM_GROUP.GOURD);

    const tasselBall = new THREE.SphereGeometry(0.008, 4, 3);
    tasselBall.translate(gourdX, gourdBaseY - 0.065, 0.025);
    parts.push(tasselBall);
    colors.push(new THREE.Color(0x8b0000));
    animGroups.push(MAGE_ANIM_GROUP.GOURD);

    return this.mergeGeometriesWithAnimData(parts, colors, animGroups);
  }

  private mergeGeometriesWithAnimData(
    geometries: THREE.BufferGeometry[],
    colors: THREE.Color[],
    animGroups: number[]
  ): THREE.BufferGeometry {
    // Calculate total vertices
    let totalVertices = 0;
    for (const geo of geometries) {
      const nonIndexed = geo.index ? geo.toNonIndexed() : geo;
      totalVertices += nonIndexed.getAttribute('position').count;
    }

    // Create output arrays
    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);
    const vertexColors = new Float32Array(totalVertices * 3);
    const animGroupAttr = new Float32Array(totalVertices);

    let offset = 0;
    for (let i = 0; i < geometries.length; i++) {
      let geo = geometries[i];
      if (geo.index) {
        geo = geo.toNonIndexed();
      }

      const posAttr = geo.getAttribute('position');
      const normalAttr = geo.getAttribute('normal');
      const color = colors[i];
      const animGroup = animGroups[i];
      const count = posAttr.count;

      // Add subtle color variation
      const variation = 0.94 + Math.random() * 0.12;

      for (let j = 0; j < count; j++) {
        const idx = offset + j;

        // Position
        positions[idx * 3] = posAttr.getX(j);
        positions[idx * 3 + 1] = posAttr.getY(j);
        positions[idx * 3 + 2] = posAttr.getZ(j);

        // Normal
        if (normalAttr) {
          normals[idx * 3] = normalAttr.getX(j);
          normals[idx * 3 + 1] = normalAttr.getY(j);
          normals[idx * 3 + 2] = normalAttr.getZ(j);
        }

        // Color with variation
        vertexColors[idx * 3] = color.r * variation;
        vertexColors[idx * 3 + 1] = color.g * variation;
        vertexColors[idx * 3 + 2] = color.b * variation;

        // Animation group
        animGroupAttr[idx] = animGroup;
      }

      offset += count;
      geometries[i].dispose();
    }

    // Create merged geometry
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    merged.setAttribute('animGroup', new THREE.BufferAttribute(animGroupAttr, 1));
    merged.computeVertexNormals();

    return merged;
  }

  addMage(entity: Entity, x: number, y: number, z: number, factionId: FactionId): void {
    if (this.entityMap.has(entity)) return;

    const data: MageData = {
      entity,
      x,
      y,
      z,
      factionId,
      rotation: 0,
      speed: 0,
      animPhase: Math.random() * Math.PI * 2,
      attackTime: -1,
      attackDuration: 0.7,
    };
    this.mageData.push(data);
    this.entityMap.set(entity, data);
  }

  removeMage(entity: Entity): void {
    const mage = this.entityMap.get(entity);
    if (!mage) return;

    const index = this.mageData.indexOf(mage);
    if (index !== -1) {
      const last = this.mageData[this.mageData.length - 1];
      this.mageData[index] = last;
      this.mageData.pop();
    }
    this.entityMap.delete(entity);
  }

  updateMage(entity: Entity, x: number, y: number, z: number, rotation?: number): void {
    const mage = this.entityMap.get(entity);
    if (!mage) return;

    // Calculate speed from movement
    const dx = x - mage.x;
    const dz = z - mage.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Smooth speed transition
    const targetSpeed = Math.min(distance * 30, 1.0);
    mage.speed = mage.speed * 0.85 + targetSpeed * 0.15;

    // Update rotation
    if (rotation !== undefined) {
      mage.rotation = rotation;
    } else if (distance > 0.001) {
      mage.rotation = Math.atan2(dx, dz);
    }

    mage.x = x;
    mage.y = y;
    mage.z = z;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update time uniform for all materials
    for (const material of this.materials.values()) {
      material.uniforms.uTime.value = this.time;
    }

    // Group mages by faction for instanced rendering
    const factionGroups = new Map<FactionId, MageData[]>();
    for (const mage of this.mageData) {
      if (!factionGroups.has(mage.factionId)) {
        factionGroups.set(mage.factionId, []);
      }
      factionGroups.get(mage.factionId)!.push(mage);
    }

    // Update each faction's instanced mesh
    for (const [factionId, mesh] of this.instancedMeshes) {
      const mageList = factionGroups.get(factionId) || [];
      mesh.count = mageList.length;

      if (mageList.length === 0) continue;

      // Update instance attributes and matrices
      for (let i = 0; i < mageList.length; i++) {
        const mage = mageList[i];

        // Update instance attributes
        if (this.speedAttribute) {
          this.speedAttribute.setX(i, mage.speed);
        }
        if (this.phaseAttribute) {
          this.phaseAttribute.setX(i, mage.animPhase);
        }
        if (this.attackTimeAttribute) {
          this.attackTimeAttribute.setX(i, mage.attackTime);
        }

        // Update transform matrix
        const surfaceOffset = 0.02;
        this.dummy.position.set(mage.x, mage.y + surfaceOffset, mage.z);
        this.dummy.rotation.set(0, mage.rotation, 0);
        this.dummy.scale.setScalar(1.75);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(i, this.dummy.matrix);
      }

      // Mark for GPU update
      mesh.instanceMatrix.needsUpdate = true;
      if (this.speedAttribute) this.speedAttribute.needsUpdate = true;
      if (this.phaseAttribute) this.phaseAttribute.needsUpdate = true;
      if (this.attackTimeAttribute) this.attackTimeAttribute.needsUpdate = true;
    }
  }

  getMageCount(): number {
    return this.mageData.length;
  }

  clear(): void {
    this.mageData.length = 0;
    for (const mesh of this.instancedMeshes.values()) {
      mesh.count = 0;
    }
  }

  dispose(): void {
    // Remove event listeners
    if (this.attackEventHandler) {
      EventBus.off('unit:attack', this.attackEventHandler);
    }

    // Dispose meshes
    for (const mesh of this.instancedMeshes.values()) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
      this.scene.remove(mesh);
    }
    this.instancedMeshes.clear();
    this.materials.clear();
    this.mageData.length = 0;
  }
}
